import { useMemo } from 'react';
import type { CellChange } from '@silevis/reactgrid';
import {
  assetTypes,
  macroAssetTypes,
  subAssetTypeFields,
  subAssetTypesFCA
} from '../../../../consts/assetTypes';
import {
  getCellWidths,
  getColumnByType,
  getRowHeight,
  getTextHeightForParagraph,
  updateDataByType
} from '../utils/functions';
import commonStore from '../../../../store/common';
import { CustomHeaderWithDrillDown } from '../../components/CustomHeaderWithDrillDown';
import { useSearchParams } from 'react-router-dom';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { useSyncExternalStore } from 'react';
import { deepCopy } from '../../../../utils/deepCopy';
import type {
  FundDataRow,
  FundEntry,
  SchemaField,
  UseTableBaseProps,
  UseIndividualExposuresReturn
} from './types';
import { isFcaTableDisabledForFund } from '../utils/visibleSections';

const tabsKey = {
  long_value: { name: 'Long value', letter: 'A' },
  short_value: { name: 'Short value', letter: 'B' },
  gross_value: { name: 'Gross value', letter: 'A' }
};

type IndividualExposureColumnKey = keyof typeof tabsKey;

interface IndividualExposureDifferenceCell {
  is_changed?: boolean;
  change_percent?: number;
  change_direction?: 'up' | 'down' | string;
  [key: string]: unknown;
}

interface IndividualExposureDifferenceRow extends Partial<
  Record<IndividualExposureColumnKey, IndividualExposureDifferenceCell>
> {
  sub_asset_type?: string;
}

interface IndividualExposureSchemaType {
  type: string;
  options?: unknown[];
  has_drill_down_feature?: boolean;
}

export type IndividualExposureSchema = SchemaField & {
  types: Record<IndividualExposureColumnKey, IndividualExposureSchemaType>;
  defaultValues: Record<IndividualExposureColumnKey, unknown>;
};

interface IndividualExposureRow extends FundDataRow {
  fund_id?: string;
  fund_name?: string;
  period?: string;
  year?: string;
  report_id?: string | number;
  id?: string | number;
  errors?: Record<string, { msg?: string } | undefined>;
  warning?: Record<string, { msg?: string } | undefined>;
}

export interface IndividualExposureFund extends FundEntry {
  fund_data: Record<string, IndividualExposureRow[]>;
  previous_fund?: IndividualExposureFund;
  period?: string;
  year?: string;
  difference?: Record<string, IndividualExposureDifferenceRow[]>;
}

interface UseIndividualExposuresProps extends Omit<UseTableBaseProps, 'data' | 'schema' | 'funds'> {
  data?: IndividualExposureRow[] | null;
  schema: IndividualExposureSchema;
  funds: Record<string, IndividualExposureFund>;
  tableName?: never;
  columnLables?: never;
}

type IndividualExposureFieldMap = Partial<Record<IndividualExposureColumnKey, null>>;
type GroupedIndividualExposureData = Record<
  string,
  Record<string, Record<string, IndividualExposureFieldMap>>
>;

type IndividualExposureCellChange = CellChange & {
  newCell: CellChange['newCell'] & {
    fundId?: string;
    sub_asset_type: string;
    dataKey: IndividualExposureColumnKey;
    value?: unknown;
    text?: string;
    checked?: boolean;
    selectedValue?: string | boolean | null;
    date?: Date | null;
    type?: string;
  };
};

const getCellWidth = (
  isMultiFunds: boolean | undefined,
  fundSize: number,
  contentWidth: number
) => {
  if (!isMultiFunds) return contentWidth / 2;
  return fundSize <= 3 ? contentWidth / (fundSize + 1) : 300;
};

const getLabelWidth = (
  isMultiFunds: boolean | undefined,
  fundSize: number,
  contentWidth: number
) => {
  if (!isMultiFunds) return contentWidth / 2;
  return fundSize <= 3 ? contentWidth / (fundSize + 1) : 200;
};

const getHeaderHeight = (
  isMultiFunds: boolean | undefined,
  text: string | undefined,
  labelWidth: number
) => {
  if (!isMultiFunds) return 40;
  return getTextHeightForParagraph(text || '', labelWidth);
};

export default function useIndividualExposures({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  funds,
  isMultiFunds
}: UseIndividualExposuresProps): UseIndividualExposuresReturn {
  const [searchParams] = useSearchParams();
  const currentDataKey = searchParams.get('fund_id')!;
  const getFundExposureRows = fund => fund?.fund_data?.INDIVIDUAL_EXPOSURES || [];
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isFcaTableDisabledForFund(fund, 'INDIVIDUAL_EXPOSURES');
  };
  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  const onChange: UseIndividualExposuresReturn['onChange'] = value => {
    const changes = value[0] as unknown as IndividualExposureCellChange;
    const _fund: IndividualExposureRow[] = isMultiFunds
      ? deepCopy(getFundExposureRows(funds[changes.newCell.fundId!]))
      : deepCopy(data);

    const index = _fund.findIndex(item => item.sub_asset_type === changes.newCell.sub_asset_type);

    if (index < 0) return;

    _fund[index][changes.newCell.dataKey] = updateDataByType({
      cell: changes.newCell
    });

    onChangeFund('INDIVIDUAL_EXPOSURES', _fund, changes.newCell.fundId);
  };

  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );

  const tables = useMemo(() => {
    if (!data) return [];

    let amountRow = 76;

    const groupedData: GroupedIndividualExposureData = {};

    Object.keys(macroAssetTypes).forEach(key => {
      groupedData[key] = {};
    });

    Object.keys(assetTypes).forEach(key => {
      const macroKey = key.split('_')[0];
      if (groupedData[macroKey]) {
        if (key !== 'DER_IRD') {
          groupedData[macroKey][key] = {};
        }
      }
    });

    Object.keys(subAssetTypesFCA).forEach(key => {
      const assetKey = key.split('_').slice(0, 2).join('_');
      const macroKey = key.split('_')[0];

      if (groupedData[macroKey] && groupedData[macroKey][assetKey]) {
        groupedData[macroKey][assetKey][key] = subAssetTypeFields[key];
      }

      if (key === 'DER_IRD_INTR') {
        groupedData['DER']['DER_FEX']['DER_IRD_INTR'] = subAssetTypeFields[key];
      }
    });

    const _fundSize = Object.keys(funds).length;

    const _tables = Object.keys(groupedData).map(macro_asset_type => {
      return {
        name: macroAssetTypes[macro_asset_type],
        tables: Object.keys(groupedData[macro_asset_type]).map(asset_type => {
          const assetType = groupedData[macro_asset_type][asset_type];
          amountRow = amountRow + Object.keys(assetType).length;
          const cellWidth = getCellWidth(isMultiFunds, _fundSize, contentWidth);
          const labelWidth = getLabelWidth(isMultiFunds, _fundSize, contentWidth);
          return {
            name: assetTypes[asset_type],
            tables: Object.keys(groupedData[macro_asset_type][asset_type]).map(
              (sub_asset_type, index) => {
                const number = amountRow - Object.keys(assetType).length + index + 1;

                return {
                  columns: isMultiFunds
                    ? _fundSize <= 3 && !isComparisonReport
                      ? [
                          {
                            columnId: 0,
                            resizable: true,
                            width: 30
                          },
                          {
                            columnId: 1,
                            resizable: true,
                            width: 30
                          },
                          ...Array.from(Array(_fundSize + 1).keys()).map(index => ({
                            columnId: index + 2,
                            width: index === 0 ? labelWidth : cellWidth
                          }))
                        ]
                      : isComparisonReport
                        ? [
                            {
                              columnId: 0,
                              resizable: true,
                              width: 30
                            },
                            {
                              columnId: 1,
                              resizable: true,
                              width: 30
                            },
                            {
                              columnId: 2,
                              width: 200
                            },
                            ...Object.values(funds)
                              .map(item => [item, item?.previous_fund])
                              .flat()
                              .map((_, index) => ({
                                columnId: index + 3,
                                width: 300
                              }))
                          ]
                        : [
                            {
                              columnId: 0,
                              resizable: true,
                              width: 30
                            },
                            {
                              columnId: 1,
                              resizable: true,
                              width: 30
                            },
                            {
                              columnId: 2,
                              width: 200
                            },
                            ...Object.keys(funds).map((_, index) => ({
                              columnId: index + 3,
                              width: 300
                            }))
                          ]
                    : isComparisonReport
                      ? [
                          {
                            columnId: 0,
                            resizable: true,
                            width: 30
                          },
                          {
                            columnId: 1,
                            resizable: true,
                            width: 30
                          },
                          ...Array.from(Array(3).keys()).map(index => ({
                            columnId: index + 2,
                            width: contentWidth / 3
                          }))
                        ]
                      : [
                          {
                            columnId: 0,
                            resizable: true,
                            width: 30
                          },
                          {
                            columnId: 1,
                            resizable: true,
                            width: 30
                          },
                          ...Array.from(Array(2).keys()).map(index => ({
                            columnId: index + 2,
                            width: contentWidth / 2
                          }))
                        ],
                  rows: [
                    {
                      height: getHeaderHeight(
                        isMultiFunds,
                        subAssetTypesFCA[sub_asset_type],
                        getLabelWidth(isMultiFunds, _fundSize, contentWidth)
                      ),
                      rowId: 0,
                      cells: isMultiFunds
                        ? [
                            {
                              type: 'header',
                              text: ''
                            },
                            {
                              type: 'header',
                              text: ''
                            },
                            {
                              type: 'header',
                              text: subAssetTypesFCA[sub_asset_type] || ''
                            },
                            ...(isComparisonReport
                              ? Object.values(funds)
                                  .map(item => [item, item.previous_fund])
                                  .flat()
                                  .map((fund, i) => ({
                                    type: 'header',
                                    text: fund?.fund_name || '',
                                    colspan: i % 2 === 0 ? 2 : undefined
                                  }))
                              : Object.values(funds).map(fund => ({
                                  type: 'header',
                                  text: fund?.fund_name || ''
                                })))
                          ]
                        : [
                            {
                              type: 'header',
                              text: ''
                            },
                            {
                              type: 'header',
                              text: ''
                            },
                            {
                              type: 'header',
                              text: subAssetTypesFCA[sub_asset_type] || ''
                            },
                            ...(isComparisonReport
                              ? [
                                  {
                                    type: 'header',
                                    text: `${funds[currentDataKey].previous_fund?.period}, ${funds[currentDataKey].previous_fund?.year}`
                                  },
                                  {
                                    type: 'header',
                                    text: `${funds[currentDataKey].period}, ${funds[currentDataKey].year}`
                                  }
                                ]
                              : [
                                  {
                                    type: 'header',
                                    text: ''
                                  }
                                ])
                          ]
                    },

                    ...(isComparisonReport && isMultiFunds
                      ? [
                          {
                            height: getHeaderHeight(
                              isMultiFunds,
                              subAssetTypesFCA[sub_asset_type],
                              getLabelWidth(isMultiFunds, _fundSize, contentWidth)
                            ),
                            rowId: 1,
                            cells: [
                              {
                                type: 'header',
                                text: ''
                              },
                              {
                                type: 'header',
                                text: ''
                              },
                              {
                                type: 'header',
                                text: ''
                              },
                              ...Object.values(funds)
                                .map(item => [item.previous_fund, item])
                                .flat()
                                .map(fund => ({
                                  type: 'header',
                                  text: `${fund?.period}, ${fund?.year}`
                                }))
                            ]
                          }
                        ]
                      : []),
                    ...(
                      Object.keys(
                        subAssetTypeFields[sub_asset_type]
                      ) as IndividualExposureColumnKey[]
                    ).map((columnKey, index2) => {
                      const schemaField = schema.types[columnKey];
                      const defaultValue = schema.defaultValues[columnKey];
                      const __value = data.find(val => val.sub_asset_type === sub_asset_type);

                      const __differenceList =
                        funds[currentDataKey]?.difference?.INDIVIDUAL_EXPOSURES;
                      const __difference = __differenceList
                        ? __differenceList.find(val => val.sub_asset_type === sub_asset_type)
                        : null;
                      const _previous_value: any = funds[
                        currentDataKey
                      ]?.previous_fund?.fund_data?.INDIVIDUAL_EXPOSURES?.find(
                        val => val.sub_asset_type === sub_asset_type
                      );

                      const cells = !isMultiFunds
                        ? [
                            {
                              type:
                                __value?.report_id &&
                                __value?.[columnKey] !== null &&
                                schemaField?.has_drill_down_feature
                                  ? 'customHeader'
                                  : 'header',
                              text: tabsKey[columnKey].name || '',
                              render: () => (
                                <CustomHeaderWithDrillDown
                                  reportId={__value?.report_id}
                                  fundId={__value?.fund_id}
                                  columnName={columnKey}
                                  tableName="INDIVIDUAL_EXPOSURES"
                                  title={tabsKey[columnKey].name || ''}
                                  columnPrettyName={subAssetTypesFCA[sub_asset_type]}
                                  tableId={__value?.id}
                                />
                              ),
                              style: {
                                'justify-content': 'flex-start',
                                'white-space': 'inherit'
                              }
                            },

                            ...(isComparisonReport
                              ? [
                                  {
                                    ...getColumnByType({
                                      type: schemaField?.type,
                                      options: schemaField?.options,
                                      key: columnKey,
                                      defaultValue,
                                      index: 0,
                                      value: _previous_value,
                                      required: requiredFields.includes(columnKey),
                                      tableName: 'INDIVIDUAL_EXPOSURES_' + sub_asset_type,
                                      isStaticTemplate,
                                      fundId: _previous_value?.fund_id,
                                      isSectionDisabled: isTableDisabledForFund(_previous_value),
                                      fundName: _previous_value?.fund_name,
                                      fieldOrder: number,
                                      nonEditable: isComparisonReport
                                    }),
                                    macro_asset_type,
                                    asset_type,
                                    sub_asset_type,
                                    isMultiFunds
                                  },
                                  {
                                    ...getColumnByType({
                                      type: schemaField?.type,
                                      options: schemaField?.options,
                                      key: columnKey,
                                      defaultValue,
                                      index: 0,
                                      value: __value,
                                      required: requiredFields.includes(columnKey),
                                      tableName: 'INDIVIDUAL_EXPOSURES_' + sub_asset_type,
                                      isStaticTemplate,
                                      fundId: __value?.fund_id,
                                      isSectionDisabled: isTableDisabledForFund(__value?.fund_id),
                                      fundName: __value?.fund_name,
                                      fieldOrder: number,
                                      difference: __difference?.[columnKey] || {},
                                      nonEditable: isComparisonReport
                                    }),
                                    macro_asset_type,
                                    asset_type,
                                    sub_asset_type,
                                    isMultiFunds
                                  }
                                ]
                              : [
                                  {
                                    ...getColumnByType({
                                      type: schemaField?.type,
                                      options: schemaField?.options,
                                      key: columnKey,
                                      defaultValue,
                                      index: 0,
                                      value: __value,
                                      required: requiredFields.includes(columnKey),
                                      tableName: 'INDIVIDUAL_EXPOSURES_' + sub_asset_type,
                                      isStaticTemplate,
                                      fundId: __value?.fund_id,
                                      fundName: __value?.fund_name,
                                      fieldOrder: number,
                                      nonEditable: isComparisonReport
                                    }),
                                    macro_asset_type,
                                    asset_type,
                                    sub_asset_type,
                                    isMultiFunds
                                  }
                                ])
                          ]
                        : [
                            {
                              type: 'header',
                              text: tabsKey[columnKey].name || '',
                              style: {
                                'justify-content': 'flex-start',
                                'white-space': 'inherit'
                              }
                            },
                            ...(isComparisonReport
                              ? Object.values(funds)
                                  .map(item => [item?.previous_fund, item])
                                  .flat()
                                  .map((value, j) => {
                                    const _fundRows = getFundExposureRows(value);
                                    const _value = _fundRows.find(
                                      val => val.sub_asset_type === sub_asset_type
                                    );

                                    const __differenceList =
                                      funds[String(value?.fund_id)]?.difference
                                        ?.INDIVIDUAL_EXPOSURES;
                                    const __difference = __differenceList
                                      ? __differenceList.find(
                                          val => val.sub_asset_type === sub_asset_type
                                        )
                                      : null;
                                    return {
                                      ...getColumnByType({
                                        type: schemaField?.type,
                                        options: schemaField?.options,
                                        key: columnKey,
                                        defaultValue,
                                        index: 0,
                                        value: _value,
                                        required: requiredFields.includes(columnKey),
                                        tableName: 'INDIVIDUAL_EXPOSURES_' + sub_asset_type,
                                        isStaticTemplate,
                                        fundId: value?.fund_id,
                                        isSectionDisabled: isTableDisabledForFund(value?.fund_id),
                                        fundName: value?.fund_name,
                                        hasDrillDownFeature:
                                          !!_value?.report_id &&
                                          _value?.[columnKey] !== null &&
                                          schemaField?.has_drill_down_feature,
                                        reportId: _value?.report_id,
                                        tableId: _value?.id,
                                        title: subAssetTypesFCA[sub_asset_type] || '',
                                        isMultiFunds,
                                        fieldOrder: number,
                                        difference:
                                          j % 2 === 1 ? __difference?.[columnKey] || {} : null,
                                        nonEditable: isComparisonReport
                                      }),
                                      macro_asset_type,
                                      asset_type,
                                      sub_asset_type
                                    };
                                  })
                              : Object.values(funds).map(value => {
                                  const _fundRows = getFundExposureRows(value);
                                  const _value = _fundRows.find(
                                    val => val.sub_asset_type === sub_asset_type
                                  );
                                  return {
                                    ...getColumnByType({
                                      type: schemaField?.type,
                                      options: schemaField?.options,
                                      key: columnKey,
                                      defaultValue,
                                      index: 0,
                                      value: _value,
                                      required: requiredFields.includes(columnKey),
                                      tableName: 'INDIVIDUAL_EXPOSURES_' + sub_asset_type,
                                      isStaticTemplate,
                                      fundId: value.fund_id,
                                      isSectionDisabled: isTableDisabledForFund(value.fund_id),
                                      fundName: value.fund_name,
                                      hasDrillDownFeature:
                                        !!_value?.report_id &&
                                        _value?.[columnKey] !== null &&
                                        schemaField?.has_drill_down_feature,
                                      reportId: _value?.report_id,
                                      tableId: _value?.id,
                                      title: subAssetTypesFCA[sub_asset_type] || '',
                                      isMultiFunds,
                                      fieldOrder: number,
                                      nonEditable: isComparisonReport
                                    }),
                                    macro_asset_type,
                                    asset_type,
                                    sub_asset_type
                                  };
                                }))
                          ];
                      const { labelWidth, valueWidth } = getCellWidths({
                        isMultiFunds,
                        isComparisonReport,
                        fundSize: _fundSize,
                        contentWidth
                      });

                      const valueCells = isMultiFunds
                        ? (isComparisonReport
                            ? Object.values(funds)
                                .map(item => [item.previous_fund, item])
                                .flat()
                            : Object.values(funds)
                          ).map(fund => {
                            const row = getFundExposureRows(fund).find(
                              item => item.sub_asset_type == sub_asset_type
                            );
                            return {
                              value: '0',
                              error: row?.errors?.[columnKey]?.msg || ''
                            };
                          })
                        : isComparisonReport
                          ? [
                              {
                                value: '0',
                                error:
                                  (
                                    _previous_value?.errors?.[columnKey] as
                                      { msg?: string } | undefined
                                  )?.msg || ''
                              },
                              {
                                value: '0',
                                error: __value?.errors?.[columnKey]?.msg || ''
                              }
                            ]
                          : [
                              {
                                value: '0',
                                error:
                                  (__value?.errors?.[columnKey] as { msg?: string } | undefined)
                                    ?.msg || ''
                              }
                            ];

                      const rowHeight = getRowHeight({
                        label: subAssetTypesFCA[sub_asset_type],
                        labelWidth,
                        cells: valueCells,
                        valueWidth
                      });
                      return {
                        rowId: isComparisonReport && isMultiFunds ? index2 + 2 : index2 + 1,
                        height: rowHeight,
                        cells: [
                          {
                            type: 'header',
                            text: `${number === 88 ? 252 : number === 89 ? 253 : number}`
                          },
                          {
                            type: 'header',
                            text: tabsKey[columnKey].letter || ''
                          },

                          ...cells
                        ]
                      };
                    })
                  ]
                };
              }
            )
          };
        })
      };
    });

    return _tables;
  }, [data, isMultiFunds, contentWidth, funds, isComparisonReport, currentDataKey]);

  return { onChange, tables };
}
